import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Typography } from '@/components/ui/typography'
import { useBoardEdit } from '@/features/ogsm/board-edit-context'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { OwnerChip } from '@/components/custom/ogsm/owner-chip'
import { useOgsmApi } from '@/hooks/use-ogsm'
import { useOgsmAssigneesQuery } from '@/hooks/use-ogsm-data'
import { cn } from '@/lib/utils'
import type { OgsmAssignee, OgsmBoardNode } from '@/types/api'

const ORGANIZATION_LABEL: Record<OgsmAssignee['organization'], string> = {
  GREYBULL: OGSM_COPY.greybull,
  COMPANY: OGSM_COPY.company,
}

const ROW =
  'flex w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-primary/5'

/** The owner chip; a popover to reassign it when the structure is editable. */
export function OwnerControl({ node }: { node: OgsmBoardNode }) {
  const { companyId, session, saveNode } = useBoardEdit()
  const [open, setOpen] = useState(false)
  // The list is fetched only when someone opens the popover.
  const assignees = useOgsmAssigneesQuery(useOgsmApi(companyId), open)
  if (!session.structureEditable) return <OwnerChip node={node} />

  const kind =
    node.depth === 0 ? OGSM_COPY.kind.objective : OGSM_COPY.kind.strategy

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={OGSM_COPY.changeOwner}
          className="inline-flex shrink-0 cursor-pointer items-center gap-0.5"
        >
          <OwnerChip node={node} />
          <ChevronDown className="size-2.5 text-primary/45" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-80 w-80 max-w-[calc(100vw-2rem)] overflow-x-hidden overflow-y-auto p-1.5"
      >
        <Typography
          variant="overline"
          className="block px-2 pt-1 pb-1.5 text-primary/60"
        >
          {OGSM_COPY.assignTo(kind)}
        </Typography>
        <button
          type="button"
          className={cn(ROW, !node.owner && 'bg-secondary/10 text-secondary')}
          onClick={() => {
            setOpen(false)
            saveNode(node.id, { ownerUserId: null, ownerContactId: null })
          }}
        >
          {OGSM_COPY.unassigned}
        </button>
        {assignees.isLoading && (
          <Typography variant="muted" className="px-2 py-1.5">
            {OGSM_COPY.loading}
          </Typography>
        )}
        {assignees.data?.map((person, index, all) => {
          const selected =
            node.owner?.type === person.type && node.owner.id === person.id
          return (
            <div key={`${person.type}-${person.id}`}>
              {person.organization !== all[index - 1]?.organization && (
                <Typography
                  variant="overline"
                  className="block px-2 pt-2 pb-1 text-primary/60"
                >
                  {ORGANIZATION_LABEL[person.organization]}
                </Typography>
              )}
              <button
                type="button"
                className={cn(
                  ROW,
                  selected && 'bg-secondary/10 text-secondary'
                )}
                onClick={() => {
                  setOpen(false)
                  saveNode(
                    node.id,
                    person.type === 'USER'
                      ? { ownerUserId: person.id }
                      : { ownerContactId: person.id }
                  )
                }}
              >
                <Typography
                  as="span"
                  variant="body-sm"
                  className="inline-flex h-5.5 min-w-8 shrink-0 px-1 items-center justify-center rounded-md border border-primary/20 bg-primary/5 font-semibold text-primary"
                >
                  {person.initials}
                </Typography>
                <span className="min-w-0 flex-1 break-words">
                  {person.name}
                </span>
                <Typography
                  as="span"
                  variant="body-sm"
                  className="shrink-0 rounded-full border border-primary/20 px-1.5 text-[9px] leading-4 text-primary/60"
                >
                  {person.roleLabel}
                </Typography>
              </button>
            </div>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}
